/**
 * Admin Config API
 *
 * Dataverse API operations for the generic AdminConfig entity.
 */

import {
  dataverseClient,
  TABLES,
  type AdminConfigEntity,
  type QueryOptions,
} from '@shared/api';
import { escapeODataString } from '@shared/api/dataverse/utils';
import type {
  AdminConfig,
  UpdateAdminConfigInput,
} from '../model/types';

// =============================================================================
// Entity Mapping
// =============================================================================

function mapToDomain<T = unknown>(entity: AdminConfigEntity): AdminConfig<T> {
  let parsedValue: T;
  try {
    parsedValue = JSON.parse(entity.vba_value) as T;
  } catch {
    parsedValue = entity.vba_value as unknown as T;
  }

  return {
    id: entity.vba_adminconfigid,
    key: entity.vba_name,
    category: entity.vba_category,
    displayName: entity.vba_displayname,
    description: entity.vba_description ?? undefined,
    value: parsedValue,
    rowVersion: (entity['@odata.etag'] as string | undefined) ?? undefined,
    modifiedOn: (entity.modifiedon as string | undefined) ?? undefined,
  };
}

function mapToEntity<T>(input: UpdateAdminConfigInput<T>): Record<string, unknown> {
  return {
    vba_value: JSON.stringify(input.value),
  };
}

// =============================================================================
// Query Building
// =============================================================================

function buildListOptions(category?: string): QueryOptions {
  const options: QueryOptions = {
    select: [
      'vba_adminconfigid', 'vba_name', 'vba_category',
      'vba_displayname', 'vba_description', 'vba_value', 'modifiedon',
    ],
    orderBy: 'vba_category asc, vba_name asc',
  };

  if (category) {
    options.filter = `vba_category eq '${escapeODataString(category)}'`;
  }

  return options;
}

// =============================================================================
// API Functions
// =============================================================================

async function listConfigs(category?: string): Promise<AdminConfig[]> {
  const options = buildListOptions(category);
  const response = await dataverseClient.list<AdminConfigEntity>(TABLES.ADMIN_CONFIG, options);
  return response.value.map((e) => mapToDomain(e));
}

async function getConfig(id: string): Promise<AdminConfig> {
  const options: QueryOptions = {
    select: [
      'vba_adminconfigid', 'vba_name', 'vba_category',
      'vba_displayname', 'vba_description', 'vba_value', 'modifiedon',
    ],
  };
  const entity = await dataverseClient.get<AdminConfigEntity>(TABLES.ADMIN_CONFIG, id, options);
  return mapToDomain(entity);
}

async function getConfigByKey<T = unknown>(key: string): Promise<AdminConfig<T> | null> {
  const options: QueryOptions = {
    select: [
      'vba_adminconfigid', 'vba_name', 'vba_category',
      'vba_displayname', 'vba_description', 'vba_value', 'modifiedon',
    ],
    filter: `vba_name eq '${escapeODataString(key)}'`,
    top: 1,
  };
  const response = await dataverseClient.list<AdminConfigEntity>(TABLES.ADMIN_CONFIG, options);
  if (response.value.length === 0) return null;
  return mapToDomain<T>(response.value[0]!);
}

async function updateConfig<T>(
  id: string,
  input: UpdateAdminConfigInput<T>,
  rowVersion?: string,
): Promise<AdminConfig<T>> {
  const entityData = mapToEntity(input);
  const options = rowVersion
    ? { ifMatch: rowVersion, returnRepresentation: true }
    : { returnRepresentation: true };
  const entity = await dataverseClient.update<AdminConfigEntity>(
    TABLES.ADMIN_CONFIG, id, entityData, options,
  );
  return mapToDomain<T>(entity as AdminConfigEntity);
}

interface CreateConfigInput<T> {
  key: string;
  category: string;
  displayName: string;
  description?: string;
  value: T;
}

async function deleteConfig(id: string): Promise<void> {
  await dataverseClient.remove(TABLES.ADMIN_CONFIG, id);
}

async function createConfig<T>(input: CreateConfigInput<T>): Promise<AdminConfig<T>> {
  const entityData: Record<string, unknown> = {
    vba_name: input.key,
    vba_category: input.category,
    vba_displayname: input.displayName,
    vba_value: JSON.stringify(input.value),
  };
  if (input.description) entityData.vba_description = input.description;
  const entity = await dataverseClient.create<AdminConfigEntity>(
    TABLES.ADMIN_CONFIG, entityData, { returnRepresentation: true },
  );
  return mapToDomain<T>(entity as AdminConfigEntity);
}

// =============================================================================
// Export
// =============================================================================

export const adminConfigApi = {
  list: listConfigs,
  get: getConfig,
  getByKey: getConfigByKey,
  update: updateConfig,
  create: createConfig,
  delete: deleteConfig,
};
